import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Simple cache for GET requests (5 minute TTL)
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Create axios instance with timeout to prevent hanging requests
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout to prevent infinite waiting
  headers: {
    'Content-Type': 'application/json'
  },
  // Limit concurrent requests
  maxRedirects: 5,
  // Disable keep-alive to prevent connection pooling issues
  httpAgent: null,
  httpsAgent: null
});

// Add auth token to requests and handle caching
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Check cache for GET requests (exclude auth/logout and real-time endpoints)
    if (config.method === 'get' &&
        !config.url.includes('/auth/logout') &&
        !config.url.includes('/dashboard') &&
        !config.url.includes('/attendance') &&
        !config.url.includes('/care-notes') &&
        !config.url.includes('/expenses') &&
        !config.url.includes('/documents') &&
        !config.url.includes('/compliance') &&
        !config.url.includes('/required-documents') &&
        !config.url.includes('/daycare') &&
        !config.url.includes('/queries') &&
        !config.url.includes('/daily-updates') &&
        !config.url.includes('/inbox') &&
        !config.url.includes('/staff-calendar') &&
        !config.url.includes('/library') &&
        !config.url.includes('/search') &&
        !config.url.includes('/export') &&
        !config.url.includes('/visitor-log')) {
      const cacheKey = `${config.url}${JSON.stringify(config.params || {})}`;
      const cached = cache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        config.adapter = () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (cached)',
          headers: {},
          config
        });
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors, cache GET responses
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses (exclude real-time endpoints)
    if (response.config.method === 'get' &&
        !response.config.url.includes('/auth/logout') &&
        !response.config.url.includes('/dashboard') &&
        !response.config.url.includes('/attendance') &&
        !response.config.url.includes('/care-notes') &&
        !response.config.url.includes('/expenses') &&
        !response.config.url.includes('/documents') &&
        !response.config.url.includes('/compliance') &&
        !response.config.url.includes('/required-documents') &&
        !response.config.url.includes('/daycare') &&
        !response.config.url.includes('/queries') &&
        !response.config.url.includes('/daily-updates') &&
        !response.config.url.includes('/inbox') &&
        !response.config.url.includes('/staff-calendar') &&
        !response.config.url.includes('/library') &&
        !response.config.url.includes('/search') &&
        !response.config.url.includes('/export') &&
        !response.config.url.includes('/visitor-log')) {
      const cacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
      cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
      
      // Limit cache size to 100 entries
      if (cache.size > 100) {
        const firstKey = cache.keys().next().value;
        cache.delete(firstKey);
      }
    }
    return response;
  },
  (error) => {
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('Request timeout - server is taking too long to respond');
      error.message = 'Request timeout. Please try again.';
    }
    
    if (error.response?.status === 401) {
      // If we're mid-impersonation (e.g. the 1h impersonation token expired),
      // restore the saved super admin session instead of bouncing to /login.
      const saToken = localStorage.getItem('sa_token');
      const saUser = localStorage.getItem('sa_user');
      if (saToken && saUser) {
        localStorage.setItem('token', saToken);
        localStorage.setItem('user', saUser);
        localStorage.removeItem('sa_token');
        localStorage.removeItem('sa_user');
        window.location.href = '/superadmin/dashboard';
      } else {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Clear relevant cache entries on mutations (POST, PUT, DELETE)
const clearCache = (url) => {
  const keysToDelete = [];
  for (const key of cache.keys()) {
    if (key.includes(url.split('?')[0])) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => cache.delete(key));
};

// Export cache clear function for manual cache invalidation
export const clearApiCache = () => cache.clear();

// Downloads a blob response by clicking a synthetic anchor tag. Keeps the
// Authorization header (unlike window.open, which drops it).
const downloadBlob = async (url, filename) => {
  const response = await api.get(url, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(blobUrl);
};

// Auth API
export const authAPI = {
  login: (email, password) => {
    clearCache('/auth');
    return api.post('/auth/login', { email, password });
  },
  logout: () => {
    clearCache('/');
    return api.post('/auth/logout');
  },
  getMe: () => api.get('/auth/me'),
  getFeatures: () => api.get('/auth/features'),
  changePassword: (currentPassword, newPassword) => {
    clearCache('/auth');
    return api.post('/auth/change-password', { currentPassword, newPassword });
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetOtp: (email, otp) => api.post('/auth/verify-reset-otp', { email, otp }),
  resetPasswordWithOtp: (email, otp, newPassword) => api.post('/auth/reset-password-otp', { email, otp, newPassword }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword })
};

// Super Admin API
export const superAdminAPI = {
  getDashboard: () => api.get('/superadmin/dashboard'),
  getSchools: (params) => api.get('/superadmin/schools', { params }),
  getSchool: (id) => api.get(`/superadmin/schools/${id}`),
  createSchool: (data) => {
    clearCache('/superadmin');
    return api.post('/superadmin/schools', data);
  },
  updateSchool: (id, data) => {
    clearCache('/superadmin');
    return api.put(`/superadmin/schools/${id}`, data);
  },
  sendPrincipalResetLink: (id) => {
    return api.put(`/superadmin/schools/${id}`, { send_principal_reset_link: true });
  },
  updateSchoolStatus: (id, status) => {
    clearCache('/superadmin');
    return api.put(`/superadmin/schools/${id}/status`, { status });
  },
  requestDeleteOtp: (id) => api.post(`/superadmin/schools/${id}/request-delete-otp`),
  deleteSchool: (id, otpCode) => {
    clearCache('/superadmin');
    return api.delete(`/superadmin/schools/${id}`, { data: { otp_code: otpCode } });
  },
  getPlatformAnalytics: () => api.get('/superadmin/analytics/platform'),
  getSchoolAnalytics: (schoolId) => api.get(`/superadmin/analytics/school/${schoolId}`),
  getStorage: () => api.get('/superadmin/storage'),
  getFeatureFlags: () => api.get('/superadmin/feature-flags'),
  updateFeatureFlag: (schoolId, featureKey, enabled) => {
    clearCache('/superadmin');
    return api.put(`/superadmin/schools/${schoolId}/features/${featureKey}`, { enabled });
  },

  // Subscription management
  getSubscriptions: (params) => api.get('/superadmin/subscriptions', { params }),
  getSchoolSubscription: (schoolId) => api.get(`/superadmin/schools/${schoolId}/subscription`),
  updateSchoolSubscription: (schoolId, data) => {
    clearCache('/superadmin');
    return api.put(`/superadmin/schools/${schoolId}/subscription`, data);
  },

  // Tier-based feature gating
  getPlanTiers: () => api.get('/superadmin/plan-tiers'),
  previewSchoolTier: (schoolId, planTier) =>
    api.get(`/superadmin/schools/${schoolId}/tier-preview`, { params: { plan_tier: planTier } }),
  applySchoolTier: (schoolId, planTier) => {
    clearCache('/superadmin');
    return api.post(`/superadmin/schools/${schoolId}/apply-tier`, { plan_tier: planTier });
  },

  // School health / churn-risk
  getHealth: () => api.get('/superadmin/health'),
  getSchoolHealth: (schoolId) => api.get(`/superadmin/schools/${schoolId}/health`),

  // API cost tracking
  getApiCosts: () => api.get('/superadmin/costs'),
  getSchoolApiCosts: (schoolId) => api.get(`/superadmin/schools/${schoolId}/costs`),

  // Audit log
  getAuditLog: (params) => api.get('/superadmin/audit-log', { params }),
  getAuditLogSummary: () => api.get('/superadmin/audit-log/summary'),

  // Impersonation
  startImpersonation: (schoolId) => api.post(`/superadmin/schools/${schoolId}/impersonate`),
  exitImpersonation: () => {
    clearApiCache();
    return api.post('/superadmin/impersonation/exit');
  },

  // Data export
  exportSchoolDataset: (schoolId, dataset, year) => {
    const yearParam = year ? `?year=${year}` : '';
    return downloadBlob(`/superadmin/schools/${schoolId}/export/${dataset}${yearParam}`, `${dataset}.csv`);
  },
  exportSchoolBundle: (schoolId, year) => {
    const yearParam = year ? `?year=${year}` : '';
    return downloadBlob(`/superadmin/schools/${schoolId}/export/bundle${yearParam}`, `classtree_export_${year || 'all'}.zip`);
  }
};

// Principal API
export const principalAPI = {
  getDashboard: () => api.get('/principal/dashboard'),
  globalSearch: (q) => api.get('/principal/search', { params: { q } }),

  // Teachers
  getTeachers: () => api.get('/principal/teachers'),
  getTeacher: (id) => api.get(`/principal/teachers/${id}`),
  createTeacher: (data) => {
    clearCache('/principal');
    return api.post('/principal/teachers', data);
  },
  updateTeacher: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/teachers/${id}`, data);
  },
  updateTeacherStatus: (id, isActive) => {
    clearCache('/principal');
    return api.put(`/principal/teachers/${id}/status`, { is_active: isActive });
  },
  deleteTeacher: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/teachers/${id}`);
  },
  resetTeacherPassword: (id) => {
    return api.put(`/principal/teachers/${id}/reset-password`);
  },
  
  // Teacher Work Hours
  getTeachersWorkHours: (params) => api.get('/principal/teachers/work-hours', { params }),
  getTeacherWorkHours: (id, params) => api.get(`/principal/teachers/${id}/work-hours`, { params }),
  recordTeacherWorkHours: (id, data) => {
    clearCache('/principal/teachers');
    return api.post(`/principal/teachers/${id}/work-hours`, data);
  },
  updateTeacherWorkHours: (id, data) => {
    clearCache('/principal/teachers');
    return api.put(`/principal/teachers/${id}/work-hours`, data);
  },
  deleteTeacherWorkHours: (id, params) => {
    clearCache('/principal/teachers');
    return api.delete(`/principal/teachers/${id}/work-hours`, { params });
  },
  
  // Students
  getStudents: (params) => api.get('/principal/students', { params }),
  getStudent: (id) => api.get(`/principal/students/${id}`),
  createStudent: (data) => {
    clearCache('/principal');
    return api.post('/principal/students', data);
  },
  updateStudent: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/students/${id}`, data);
  },
  deleteStudent: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/students/${id}`);
  },
  
  // Classes
  getClasses: () => api.get('/principal/classes'),
  createClass: (data) => {
    clearCache('/principal');
    return api.post('/principal/classes', data);
  },
  updateClass: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/classes/${id}`, data);
  },
  deleteClass: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/classes/${id}`);
  },
  
  // Staff
  getStaff: () => api.get('/principal/staff'),
  createStaff: (data) => {
    clearCache('/principal');
    return api.post('/principal/staff', data);
  },
  updateStaff: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/staff/${id}`, data);
  },
  deleteStaff: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/staff/${id}`);
  },
  
  // Attendance & Meetings
  getAttendance: (params) => api.get('/principal/attendance', { params }),
  getStudentAttendance: (date) => api.get('/principal/attendance/students', { params: { date } }),
  getAttendanceAnalytics: (params) => api.get('/principal/analytics/attendance', { params }),
  getStudentAttendanceHistory: (studentId, params) => api.get(`/principal/analytics/student-attendance/${studentId}`, { params }),
  getMeetings: (params) => api.get('/principal/meetings', { params }),
  
  // Weekly Reports
  getWeeklyReport: (classId, week) => api.get(`/principal/weekly-report/${classId}/${week}`),
  
  // Marks
  getMarks: (params) => api.get('/principal/marks', { params }),

  // Exams
  getExams: () => api.get('/principal/exams'),
  getExam: (id) => api.get(`/principal/exams/${id}`),
  createExam: (data) => {
    clearCache('/principal');
    return api.post('/principal/exams', data);
  },
  updateExam: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/exams/${id}`, data);
  },
  deleteExam: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/exams/${id}`);
  },

  // Finance
  getFinanceDashboard: () => api.get('/principal/finance/dashboard'),
  getFinanceClasses: () => api.get('/principal/finance/classes'),
  getAllStudentsWithFeeStatus: (classId) => api.get(`/principal/finance/classes/${classId}/all-students`),
  getClassStudentsPaymentStatus: (classId) => api.get(`/principal/finance/classes/${classId}/students`),
  getStudentPaymentSummary: (studentId) => api.get(`/principal/finance/student/${studentId}/summary`),
  getFees: (params) => api.get('/principal/finance/fees', { params }),
  createFee: (data) => {
    clearCache('/principal');
    return api.post('/principal/finance/fees', data);
  },
  updateFee: (feeId, data) => {
    clearCache('/principal');
    return api.put(`/principal/finance/fees/${feeId}`, data);
  },
  deleteFee: (feeId) => {
    clearCache('/principal');
    return api.delete(`/principal/finance/fees/${feeId}`);
  },
  recordPayment: (data) => {
    clearCache('/principal');
    return api.post('/principal/finance/payments', data);
  },
  getPayments: (params) => api.get('/principal/finance/payments', { params }),

  // Fee Structures
  getFeeStructures: () => api.get('/principal/finance/fee-structures'),
  createFeeStructure: (data) => {
    clearCache('/principal/finance/fee-structures');
    return api.post('/principal/finance/fee-structures', data);
  },
  updateFeeStructure: (id, data) => {
    clearCache('/principal/finance/fee-structures');
    return api.put(`/principal/finance/fee-structures/${id}`, data);
  },
  deleteFeeStructure: (id) => {
    clearCache('/principal/finance/fee-structures');
    return api.delete(`/principal/finance/fee-structures/${id}`);
  },
  bulkAssignFeeStructure: (id) => {
    clearCache('/principal');
    return api.post(`/principal/finance/fee-structures/${id}/bulk-assign`);
  },
  sendFeeReminders: () => api.post('/principal/finance/send-fee-reminders'),

  // Daycare Finance
  getDaycareDues: (params) => api.get('/principal/finance/daycare-dues', { params }),
  getDaycareFinanceSummary: () => api.get('/principal/finance/daycare-summary'),

  // School Admins (Principal only)
  getSchoolAdmins: () => api.get('/principal/school-admins'),
  createSchoolAdmin: (data) => {
    clearCache('/principal');
    return api.post('/principal/school-admins', data);
  },
  updateSchoolAdmin: (id, data) => {
    clearCache('/principal');
    return api.put(`/principal/school-admins/${id}`, data);
  },
  deleteSchoolAdmin: (id) => {
    clearCache('/principal');
    return api.delete(`/principal/school-admins/${id}`);
  },
  resetSchoolAdminPassword: (id) => {
    return api.put(`/principal/school-admins/${id}/reset-password`);
  },

  // Leave Management
  getLeaveBalances: (params) => api.get('/principal/leave-balances', { params }),
  setLeaveBalance: (data) => {
    clearCache('/principal');
    return api.post('/principal/leave-balances', data);
  },
  getLeaveRequests: (params) => api.get('/principal/leave-requests', { params }),
  reviewLeaveRequest: (id, data) => {
    clearCache('/principal');
    clearCache('/teacher'); // Teacher's balance cache must also be invalidated
    return api.put(`/principal/leave-requests/${id}/review`, data);
  },

  // Holiday Calendar
  getHolidays: (params) => api.get('/principal/holidays', { params }),
  createHoliday: (data) => {
    clearCache('/principal/holidays');
    return api.post('/principal/holidays', data);
  },
  updateHoliday: (id, data) => {
    clearCache('/principal/holidays');
    return api.put(`/principal/holidays/${id}`, data);
  },
  deleteHoliday: (id) => {
    clearCache('/principal/holidays');
    return api.delete(`/principal/holidays/${id}`);
  },

  // Announcements
  getAnnouncements: () => api.get('/principal/announcements'),
  createAnnouncement: (data) => {
    clearCache('/principal/announcements');
    return api.post('/principal/announcements', data);
  },
  updateAnnouncement: (id, data) => {
    clearCache('/principal/announcements');
    return api.put(`/principal/announcements/${id}`, data);
  },
  deleteAnnouncement: (id) => {
    clearCache('/principal/announcements');
    return api.delete(`/principal/announcements/${id}`);
  },

  // Notifications (always fresh â€” never cached)
  getNotifications: () => api.get('/principal/notifications', { params: { _t: Date.now() } }),
  markNotificationRead: (id) => api.put(`/principal/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/principal/notifications/read-all'),

  // Report Cards
  getGradeConfig: () => api.get('/principal/report-cards/grade-config'),
  saveGradeConfig: (grades) => {
    clearCache('/principal/report-cards');
    return api.put('/principal/report-cards/grade-config', { grades });
  },
  getTermStatus: (examId, classId) =>
    api.get('/principal/report-cards/term-status', { params: { exam_id: examId, class_id: classId } }),
  setTermStatus: (examId, classId, status) => {
    clearCache('/principal/report-cards');
    return api.post('/principal/report-cards/term-status', { exam_id: examId, class_id: classId, status });
  },
  getClassReportCards: (examId, classId) =>
    api.get('/principal/report-cards/class', { params: { exam_id: examId, class_id: classId } }),
  getStudentReportCard: (examId, studentId) =>
    api.get('/principal/report-cards/student', { params: { exam_id: examId, student_id: studentId } }),
  saveRemark: (studentId, examId, remarks) => {
    clearCache('/principal/report-cards');
    return api.put('/principal/report-cards/remark', { student_id: studentId, exam_id: examId, remarks });
  },
  // Academic Calendar
  getCalendarEvents: (params) => api.get('/principal/calendar', { params }),
  createCalendarEvent: (data) => {
    clearCache('/principal/calendar');
    return api.post('/principal/calendar', data);
  },
  updateCalendarEvent: (id, data) => {
    clearCache('/principal/calendar');
    return api.put(`/principal/calendar/${id}`, data);
  },
  deleteCalendarEvent: (id) => {
    clearCache('/principal/calendar');
    return api.delete(`/principal/calendar/${id}`);
  },

  // Class Timetable
  getTimetable: (classId) => api.get('/principal/timetable', { params: { class_id: classId } }),
  upsertTimetableSlot: (data) => {
    clearCache('/principal/timetable');
    return api.post('/principal/timetable', data);
  },
  deleteTimetableSlot: (classId, day, period) => {
    clearCache('/principal/timetable');
    return api.delete(`/principal/timetable/${classId}/${day}/${period}`);
  },

  // Inventory
  inventory: {
    list:         (params)     => api.get('/principal/inventory', { params }),
    get:          (id)         => api.get(`/principal/inventory/${id}`),
    summary:      ()           => api.get('/principal/inventory/summary'),
    lowStock:     ()           => api.get('/principal/inventory/low-stock'),
    transactions: (id, params) => api.get(`/principal/inventory/${id}/transactions`, { params }),
    create: (data) => {
      clearCache('/principal/inventory');
      return api.post('/principal/inventory', data);
    },
    update: (id, data) => {
      clearCache('/principal/inventory');
      return api.put(`/principal/inventory/${id}`, data);
    },
    remove: (id) => {
      clearCache('/principal/inventory');
      return api.delete(`/principal/inventory/${id}`);
    },
    adjustStock: (id, data) => {
      clearCache('/principal/inventory');
      return api.post(`/principal/inventory/${id}/stock`, data);
    },
    listSizes: (itemId) => api.get(`/principal/inventory/${itemId}/sizes`),
    addSize: (itemId, data) => {
      clearCache('/principal/inventory');
      return api.post(`/principal/inventory/${itemId}/sizes`, data);
    },
    updateSize: (itemId, sizeId, data) => {
      clearCache('/principal/inventory');
      return api.put(`/principal/inventory/${itemId}/sizes/${sizeId}`, data);
    },
    deleteSize: (itemId, sizeId) => {
      clearCache('/principal/inventory');
      return api.delete(`/principal/inventory/${itemId}/sizes/${sizeId}`);
    },
  },

  expenses: {
    list:    (params) => api.get('/principal/expenses', { params }),
    summary: ()       => api.get('/principal/expenses/summary'),
    approve: (id, note) => {
      clearCache('/principal');
      return api.put(`/principal/expenses/${id}/approve`, { note });
    },
    reject: (id, note) => {
      clearCache('/principal');
      return api.put(`/principal/expenses/${id}/reject`, { note });
    },
    pay: (id, payment_method, payment_reference) => {
      clearCache('/principal');
      return api.put(`/principal/expenses/${id}/pay`, { payment_method, payment_reference });
    },
  },

  documents: {
    list: (studentId) =>
      api.get(`/principal/students/${studentId}/documents`),
    upload: (studentId, formData) =>
      api.post(`/principal/students/${studentId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => { clearCache('/principal'); return r; }),
    download: (studentId, docId) =>
      api.get(`/principal/students/${studentId}/documents/${docId}/download`, { responseType: 'blob' }),
    remove: (studentId, docId) => {
      clearCache('/principal');
      return api.delete(`/principal/students/${studentId}/documents/${docId}`);
    },
    updateStatus: (studentId, docId, status) => {
      clearCache('/principal');
      return api.put(`/principal/students/${studentId}/documents/${docId}/status`, { status });
    },
    getRequired: () =>
      api.get('/principal/required-documents'),
    setRequired: (types) => {
      clearCache('/principal/required-documents');
      return api.put('/principal/required-documents', { types });
    },
    compliance: (params) =>
      api.get('/principal/documents/compliance', { params }),
  },

  downloadReportCardPDF: (examId, studentId) => {
    const token = localStorage.getItem('token');
    const base = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
    const url = `${base}/principal/report-cards/download-pdf?exam_id=${examId}&student_id=${studentId}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', '');
    // attach auth header via a signed fetch so the browser gets the file
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  },

  daycare: {
    getSettings:        ()               => api.get('/principal/daycare/settings'),
    updateSettings:     (data)           => { clearCache('/principal/daycare'); return api.put('/principal/daycare/settings', data); },
    listEnrollments:    ()               => api.get('/principal/daycare/enrollments'),
    enrollStudent:      (data)           => { clearCache('/principal/daycare'); return api.post('/principal/daycare/enrollments', data); },
    updateEnrollment:   (id, data)       => { clearCache('/principal/daycare'); return api.put(`/principal/daycare/enrollments/${id}`, data); },
    getDues:            ()               => api.get('/principal/daycare/dues'),
    getAnalytics:       ()               => api.get('/principal/daycare/analytics'),
    getStudentSessions: (studentId, params) => api.get(`/principal/daycare/students/${studentId}/sessions`, { params }),
    getStudentProfile:  (studentId, params) => api.get(`/principal/daycare/students/${studentId}/profile`, { params }),
    getRoster:          ()               => api.get('/principal/daycare/roster'),
    getAutoClosed:      ()               => api.get('/principal/daycare/auto-closed'),
    checkIn:            (data)           => { clearCache('/principal/daycare'); return api.post('/principal/daycare/checkin', data); },
    checkOut:           (data)           => { clearCache('/principal/daycare'); return api.post('/principal/daycare/checkout', data); },
    editSession:        (id, data)       => { clearCache('/principal/daycare'); return api.put(`/principal/daycare/sessions/${id}`, data); },
    getCareNotes:       ()               => api.get('/principal/daycare/care-notes'),
    acknowledgeCareNote: (id)            => { clearCache('/principal/daycare'); return api.put(`/principal/daycare/care-notes/${id}/acknowledge`); },
    completeCareNote:   (id, completion_remark) => { clearCache('/principal/daycare'); return api.put(`/principal/daycare/care-notes/${id}/complete`, { completion_remark }); },
    getDailyNotes:      ()               => api.get('/principal/daycare/daily-notes'),
    postDailyNote:      (data)           => { clearCache('/principal/daycare'); return api.post('/principal/daycare/daily-notes', data); },
    editDailyNote:      (id, data)       => { clearCache('/principal/daycare'); return api.put(`/principal/daycare/daily-notes/${id}`, data); },
    deleteDailyNote:    (id)             => { clearCache('/principal/daycare'); return api.delete(`/principal/daycare/daily-notes/${id}`); },
    notifyParent:       (data)           => api.post('/principal/daycare/notify-parent', data),
  },

  visitorLog: {
    list:     (params) => api.get('/principal/visitor-log', { params }),
    summary:  ()        => api.get('/principal/visitor-log/summary'),
    create:   (data)    => { clearCache('/principal/visitor-log'); return api.post('/principal/visitor-log', data); },
    checkout: (id)       => { clearCache('/principal/visitor-log'); return api.put(`/principal/visitor-log/${id}/checkout`); },
    comment:  (id, comments) => { clearCache('/principal/visitor-log'); return api.put(`/principal/visitor-log/${id}/comment`, { comments }); },
  },

  queries: {
    queue:  (params) => api.get('/queries/queue', { params }),
    void:   (id)     => { clearCache('/queries'); return api.delete(`/queries/${id}`); },
    start:  (id)     => { clearCache('/queries'); return api.put(`/queries/${id}/start`); },
    complete: (id, resolution_note) => { clearCache('/queries'); return api.post(`/queries/${id}/complete`, { resolution_note }); },
  },

  inbox: {
    getConversations:  ()            => api.get('/inbox/conversations'),
    startConversation: (data)        => { clearCache('/inbox'); return api.post('/inbox/conversations', data); },
    getMessages:       (id, params)  => api.get(`/inbox/conversations/${id}/messages`, { params }),
    sendMessage:       (id, data)    => { clearCache('/inbox'); return api.post(`/inbox/conversations/${id}/messages`, data); },
    markRead:          (id)          => { clearCache('/inbox'); return api.put(`/inbox/conversations/${id}/read`); },
    deleteMessage:     (msgId)       => { clearCache('/inbox'); return api.delete(`/inbox/messages/${msgId}`); },
    getDirectory:      ()            => api.get('/inbox/directory'),
    getUnreadCount:    ()            => api.get('/inbox/unread-count'),
  },

  staffCalendar: {
    getFeed:           (params)      => api.get('/staff-calendar/feed', { params }),
    getMyMeetings:     (params)      => api.get('/staff-calendar/meetings', { params }),
    getTodayMeetings:  ()            => api.get('/staff-calendar/meetings/today'),
    getSchoolMeetings: (params)      => api.get('/staff-calendar/meetings/school', { params }),
    getMeeting:        (id)          => api.get(`/staff-calendar/meetings/${id}`),
    createMeeting:     (data)        => { clearCache('/staff-calendar'); return api.post('/staff-calendar/meetings', data); },
    updateMeeting:     (id, data)    => { clearCache('/staff-calendar'); return api.put(`/staff-calendar/meetings/${id}`, data); },
    cancelMeeting:     (id)          => { clearCache('/staff-calendar'); return api.delete(`/staff-calendar/meetings/${id}`); },
    completeMeeting:   (id)          => { clearCache('/staff-calendar'); return api.post(`/staff-calendar/meetings/${id}/complete`); },
    getDirectory:      ()            => api.get('/staff-calendar/directory'),
    getWorkingHours:   ()            => api.get('/staff-calendar/working-hours'),
    setWorkingHours:   (data)        => { clearCache('/staff-calendar'); return api.put('/staff-calendar/working-hours', data); },
  },

  library: {
    listBooks:  (params)     => api.get('/library/books', { params }),
    getSummary: ()           => api.get('/library/books/summary'),
    getBook:    (id)         => api.get(`/library/books/${id}`),
    createBook: (data)       => { clearCache('/library'); return api.post('/library/books', data); },
    updateBook: (id, data)   => { clearCache('/library'); return api.put(`/library/books/${id}`, data); },
    deleteBook: (id)         => { clearCache('/library'); return api.delete(`/library/books/${id}`); },
    bookLoans:  (id)         => api.get(`/library/books/${id}/loans`),
    openLoans:  (params)     => api.get('/library/loans/open', { params }),
    issueBook:  (data)       => { clearCache('/library'); return api.post('/library/loans', data); },
    returnLoan: (loanId)     => { clearCache('/library'); return api.put(`/library/loans/${loanId}/return`); },
    markLost:   (loanId)     => { clearCache('/library'); return api.put(`/library/loans/${loanId}/lost`); },
    studentLoans: (studentId) => api.get(`/library/students/${studentId}/loans`),
  },

  // Data export (principal self-service)
  exportDataset: (dataset, year) => {
    const yearParam = year ? `?year=${year}` : '';
    return downloadBlob(`/principal/export/${dataset}${yearParam}`, `${dataset}.csv`);
  },
  exportBundle: (year) => {
    const yearParam = year ? `?year=${year}` : '';
    return downloadBlob(`/principal/export/bundle${yearParam}`, `classtree_export_${year || 'all'}.zip`);
  },
};

// Teacher API
export const teacherAPI = {
  getDashboard: () => api.get('/teacher/dashboard'),
  getMySchedule: () => api.get('/teacher/my-schedule'),
  getCalendarEvents: (params) => api.get('/teacher/calendar', { params }),
  getClasses: () => api.get('/teacher/classes'),
  getStudents: () => api.get('/teacher/students'),
  
  // Attendance
  startWork: () => {
    clearCache('/teacher');
    return api.post('/teacher/attendance/checkin');
  },
  checkOut: () => {
    clearCache('/teacher');
    return api.post('/teacher/attendance/checkout');
  },
  getTodayAttendance: () => api.get('/teacher/attendance/today'),
  getAttendanceHistory: (params) => api.get('/teacher/attendance/history', { params }),
  getAttendanceStats: (params) => api.get('/teacher/attendance/stats', { params }),
  
  // Subjects
  getClassSubjects: (classId) => api.get(`/teacher/classes/${classId}/subjects`),
  createSubject: (classId, data) => {
    clearCache('/teacher');
    return api.post(`/teacher/classes/${classId}/subjects`, data);
  },
  deleteSubject: (subjectId) => {
    clearCache('/teacher');
    return api.delete(`/teacher/subjects/${subjectId}`);
  },
  
  // Weekly Plans
  getWeeklyPlans: (classId, params) => api.get(`/teacher/classes/${classId}/weekly-plans`, { params }),
  saveWeeklyPlan: (classId, data) => {
    clearCache('/teacher');
    return api.post(`/teacher/classes/${classId}/weekly-plans`, data);
  },
  
  // Marks
  getClassMarks: (classId, examId) => api.get(`/teacher/classes/${classId}/marks`, { params: examId ? { exam_id: examId } : {} }),
  saveMarks: (data) => {
    clearCache('/teacher');
    return api.post('/teacher/marks', data);
  },
  
  // Student Attendance
  getStudentsForAttendance: (classId, date) => api.get(`/teacher/attendance/class/${classId}`, { params: { date } }),
  markAttendance: (data) => {
    clearCache('/teacher');
    return api.post('/teacher/attendance/mark', data);
  },
  
  // Exam Scheduling
  getClassExams: (classId) => api.get(`/teacher/classes/${classId}/exams`),
  getExamSchedule: (classId, examId) => api.get(`/teacher/classes/${classId}/exams/${examId}/schedule`),
  setExamSchedule: (classId, examId, data) => api.post(`/teacher/classes/${classId}/exams/${examId}/schedule`, data),
  
  // Parent-Teacher Meetings
  scheduleClassMeetings: (classId, data) => {
    clearCache('/teacher');
    return api.post(`/teacher/classes/${classId}/schedule-meetings`, data);
  },
  bulkCreateMeetings: (data) => {
    clearCache('/teacher');
    return api.post('/teacher/meetings/bulk-create', data);
  },
  getClassMeetings: (classId) => api.get(`/teacher/classes/${classId}/meetings`),
  getClassStudents: (classId) => api.get(`/teacher/classes/${classId}/students`),
  getAllMeetings: () => api.get('/teacher/meetings'),
  rescheduleMeeting: (meetingId, data) => {
    clearCache('/teacher');
    return api.put(`/teacher/meetings/${meetingId}/reschedule`, data);
  },
  cancelMeeting: (meetingId) => {
    clearCache('/teacher');
    return api.put(`/teacher/meetings/${meetingId}/cancel`);
  },
  completeMeeting: (meetingId, notes) => {
    clearCache('/teacher');
    return api.put(`/teacher/meetings/${meetingId}/complete`, { notes });
  },

  // Leave Management
  getLeaveBalance: () => api.get('/teacher/leave-balance'),
  getLeaveRequests: () => api.get('/teacher/leave-requests'),
  applyLeave: (data) => {
    clearCache('/teacher');
    return api.post('/teacher/leave-requests', data);
  },

  // Holiday Calendar (read-only)
  getHolidays: (params) => api.get('/teacher/holidays', { params }),

  // Announcements (read-only)
  getAnnouncements: () => api.get('/teacher/announcements'),

  // Notifications (always fresh — never cached)
  getNotifications: () => api.get('/teacher/notifications', { params: { _t: Date.now() } }),
  markNotificationRead: (id) => api.put(`/teacher/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/teacher/notifications/read-all'),
  careNotes: {
    today:       (date) => api.get('/teacher/care-notes/today', { params: { date } }),
    acknowledge: (id)   => api.put(`/teacher/care-notes/${id}/acknowledge`).then(r => { clearCache('/teacher'); return r; }),
    complete:    (id, completion_remark) =>
      api.put(`/teacher/care-notes/${id}/complete`, { completion_remark }).then(r => { clearCache('/teacher'); return r; })
  },
  expenses: {
    list:   (status) => api.get('/teacher/expenses', { params: status ? { status } : {} }),
    create: (data) => {
      clearCache('/teacher');
      return api.post('/teacher/expenses', data);
    },
    update: (id, data) => {
      clearCache('/teacher');
      return api.put(`/teacher/expenses/${id}`, data);
    },
    remove: (id) => {
      clearCache('/teacher');
      return api.delete(`/teacher/expenses/${id}`);
    },
  },

  daycare: {
    getRoster:    ()        => api.get('/teacher/daycare/roster'),
    getAutoClosed: ()       => api.get('/teacher/daycare/auto-closed'),
    checkIn:      (data)    => { clearCache('/teacher/daycare'); return api.post('/teacher/daycare/checkin', data); },
    checkOut:     (data)    => { clearCache('/teacher/daycare'); return api.post('/teacher/daycare/checkout', data); },
    editSession:  (id, data) => { clearCache('/teacher/daycare'); return api.put(`/teacher/daycare/sessions/${id}`, data); },
    getCareNotes: ()        => api.get('/teacher/daycare/care-notes'),
    acknowledgeCareNote: (id) => { clearCache('/teacher/daycare'); return api.put(`/teacher/daycare/care-notes/${id}/acknowledge`); },
    completeCareNote: (id, completion_remark) => { clearCache('/teacher/daycare'); return api.put(`/teacher/daycare/care-notes/${id}/complete`, { completion_remark }); },
    notifyParent: (data)    => api.post('/teacher/daycare/notify-parent', data),
  },

  queries: {
    queue:    (params)                   => api.get('/queries/queue', { params }),
    start:    (id)                       => { clearCache('/queries'); return api.put(`/queries/${id}/start`); },
    complete: (id, resolution_note)      => { clearCache('/queries'); return api.post(`/queries/${id}/complete`, { resolution_note }); },
  },

  dailyUpdates: {
    my:     (params)       => api.get('/daily-updates/my', { params }),
    eligibleStudents: ()   => api.get('/daily-updates/eligible-students'),
    post:   (data)         => { clearCache('/daily-updates'); return api.post('/daily-updates', data); },
    edit:   (id, data)     => { clearCache('/daily-updates'); return api.put(`/daily-updates/${id}`, data); },
    remove: (id)           => { clearCache('/daily-updates'); return api.delete(`/daily-updates/${id}`); },
    forStudent: (studentId, params) => api.get(`/daily-updates/student/${studentId}`, { params }),
  },

  inbox: {
    getConversations:  ()            => api.get('/inbox/conversations'),
    startConversation: (data)        => { clearCache('/inbox'); return api.post('/inbox/conversations', data); },
    getMessages:       (id, params)  => api.get(`/inbox/conversations/${id}/messages`, { params }),
    sendMessage:       (id, data)    => { clearCache('/inbox'); return api.post(`/inbox/conversations/${id}/messages`, data); },
    markRead:          (id)          => { clearCache('/inbox'); return api.put(`/inbox/conversations/${id}/read`); },
    deleteMessage:     (msgId)       => { clearCache('/inbox'); return api.delete(`/inbox/messages/${msgId}`); },
    getDirectory:      ()            => api.get('/inbox/directory'),
    getUnreadCount:    ()            => api.get('/inbox/unread-count'),
  },

  staffCalendar: {
    getFeed:           (params)      => api.get('/staff-calendar/feed', { params }),
    getMyMeetings:     (params)      => api.get('/staff-calendar/meetings', { params }),
    getTodayMeetings:  ()            => api.get('/staff-calendar/meetings/today'),
    getSchoolMeetings: (params)      => api.get('/staff-calendar/meetings/school', { params }),
    getMeeting:        (id)          => api.get(`/staff-calendar/meetings/${id}`),
    createMeeting:     (data)        => { clearCache('/staff-calendar'); return api.post('/staff-calendar/meetings', data); },
    updateMeeting:     (id, data)    => { clearCache('/staff-calendar'); return api.put(`/staff-calendar/meetings/${id}`, data); },
    cancelMeeting:     (id)          => { clearCache('/staff-calendar'); return api.delete(`/staff-calendar/meetings/${id}`); },
    completeMeeting:   (id)          => { clearCache('/staff-calendar'); return api.post(`/staff-calendar/meetings/${id}/complete`); },
    getDirectory:      ()            => api.get('/staff-calendar/directory'),
    getWorkingHours:   ()            => api.get('/staff-calendar/working-hours'),
    setWorkingHours:   (data)        => { clearCache('/staff-calendar'); return api.put('/staff-calendar/working-hours', data); },
  },

  library: {
    listBooks: (params) => api.get('/library/books', { params }),
    openLoans: (params) => api.get('/library/loans/open', { params }),
    issueBook: (data)   => { clearCache('/library'); return api.post('/library/loans', data); },
    returnLoan: (loanId) => { clearCache('/library'); return api.put(`/library/loans/${loanId}/return`); },
    markLost:   (loanId) => { clearCache('/library'); return api.put(`/library/loans/${loanId}/lost`); },
  },
};

// Meeting API
export const meetingAPI = {
  getMeetings: (params) => api.get('/meetings', { params }),
  getMeeting: (id) => api.get(`/meetings/${id}`),
  createMeeting: (data) => api.post('/meetings', data),
  updateMeeting: (id, data) => api.put(`/meetings/${id}`, data),
  deleteMeeting: (id) => api.delete(`/meetings/${id}`),
  completeMeeting: (id, notes) => api.post(`/meetings/${id}/complete`, { notes }),
  getUpcomingMeetings: () => api.get('/meetings/upcoming')
};

// Parent API
export const parentAPI = {
  getDashboard: () => api.get('/parent/dashboard'),
  getChildren: () => api.get('/parent/children'),
  getChildAttendance: (studentId, params) => api.get(`/parent/children/${studentId}/attendance`, { params }),
  getChildMarks: (studentId) => api.get(`/parent/children/${studentId}/marks`),
  getChildFees: (studentId) => api.get(`/parent/children/${studentId}/fees`),
  getMeetings: (params) => api.get('/parent/meetings', { params }),
  getCalendarEvents: (params) => api.get('/parent/calendar', { params }),
  getNotifications: () => api.get('/parent/notifications', { params: { _t: Date.now() } }),
  markNotificationRead: (id) => api.put(`/parent/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/parent/notifications/read-all'),
  careNotes: {
    list:   (params) => api.get('/parent/care-notes', { params }),
    create: (data)   => api.post('/parent/care-notes', data).then(r => { clearCache('/parent'); return r; }),
    update: (id, d)  => api.put(`/parent/care-notes/${id}`, d).then(r => { clearCache('/parent'); return r; }),
    remove: (id)     => api.delete(`/parent/care-notes/${id}`).then(r => { clearCache('/parent'); return r; })
  },
  documents: {
    list: (studentId) =>
      api.get(`/parent/children/${studentId}/documents`),
    upload: (studentId, formData) =>
      api.post(`/parent/children/${studentId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => { clearCache('/parent'); return r; }),
    download: (studentId, docId) =>
      api.get(`/parent/children/${studentId}/documents/${docId}/download`, { responseType: 'blob' }),
  },

  daycare: {
    getChild: (studentId, params) => api.get(`/parent/children/${studentId}/daycare`, { params }),
  },

  queries: {
    list:   ()             => api.get('/queries/mine'),
    create: (data)         => { clearCache('/queries'); return api.post('/queries', data); },
  },

  dailyUpdates: {
    forStudent: (studentId, params) => api.get(`/daily-updates/student/${studentId}`, { params }),
  },
};

// Chatbot API
export const chatbotAPI = {
  askQuestion: (message, history = []) => api.post('/chatbot/ask', { message, history }),
  getAnalytics: () => api.get('/chatbot/analytics')
};

export default api;
